import axios from 'axios';

const SMARTY_KEY = import.meta.env.VITE_SMARTY_KEY;
const AUTOCOMPLETE_URL = 'https://us-autocomplete.api.smarty.com/v2/lookup';

// Smarty caps the `search` value at 32 characters (and 127 bytes). Anything
// longer is rejected with a 422 — e.g. a pasted blob of personal/business
// details. Truncate before sending so oversized input never reaches the API.
// See https://www.smarty.com/docs/cloud/us-autocomplete-pro-api
const MAX_SEARCH_CHARS = 32;

export interface SmartySuggestion {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
}

const fetchSuggestions = async (params: Record<string, string>): Promise<SmartySuggestion[]> => {
  if (!SMARTY_KEY) return [];
  try {
    const { data } = await axios.get<{ suggestions?: SmartySuggestion[] }>(AUTOCOMPLETE_URL, {
      params: { key: SMARTY_KEY, max_results: '10', ...params },
    });
    return data.suggestions ?? [];
  } catch {
    return [];
  }
};

export const fetchAddressSuggestions = async (input: string): Promise<SmartySuggestion[]> => {
  if (input.length < 3) return [];
  return fetchSuggestions({ search: input.slice(0, MAX_SEARCH_CHARS), source: 'postal' });
};

export const fetchSecondaryAddressSuggestions = async (
  parent: SmartySuggestion,
  search: string
): Promise<SmartySuggestion[]> => {
  // Smarty's secondary-expansion contract requires a non-empty `search` ("the
  // value of the search UI field") and a `selected` value formatted as
  // `street_line secondary (entries) city state zipcode`, assembled from the
  // suggestion the user clicked. See https://www.smarty.com/docs/cloud/us-autocomplete-pro-api
  const secondary = parent.secondary ? `${parent.secondary} ` : '';
  const selected = `${parent.street_line} ${secondary}(${parent.entries}) ${parent.city} ${parent.state} ${parent.zipcode}`;
  return fetchSuggestions({ search: search.slice(0, MAX_SEARCH_CHARS), selected });
};
